import { useNotifications } from '@hooks/useNotifications'
import {
  FileIcon,
  HeartFillIcon,
  HomeIcon,
  MailIcon,
  MapIcon,
  NotificationIcon,
  QuestionIcon,
  UserIcon,
} from '@svgs'
import clsx from 'clsx'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { useGetChatsInfiniteQuery } from 'src/redux/socketApi'
import { useAppSelector } from 'src/redux/hooks'

interface INavigation {
  collapsed?: boolean
}

export interface IMenuItem {
  href: string
  icon: JSX.Element
  text: string
  isActive?: boolean
  notificationCount?: number
  // Plays a one-shot pop when the user saves a listing (owner feedback).
  pulse?: boolean
}

const Navigation: FC<INavigation> = ({ collapsed = false }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { unreadCount } = useNotifications(10)
  const { data } = useGetChatsInfiniteQuery({ limit: 20 })

  // One-shot pop on the "Вибране" item when a listing is saved anywhere.
  // Only react to a real INCREMENT of the counter — comparing against the
  // previous value skips the mount, so navigating to another page or collapsing
  // the side menu (which re-mounts this nav) no longer replays the animation.
  const favoritePulse = useAppSelector((state) => state.ui.favoritePulse)
  const prevPulseRef = useRef(favoritePulse)
  const [pulsing, setPulsing] = useState(false)
  useEffect(() => {
    if (favoritePulse > prevPulseRef.current) {
      prevPulseRef.current = favoritePulse
      setPulsing(true)
      const timer = setTimeout(() => setPulsing(false), 600)
      return () => clearTimeout(timer)
    }
    prevPulseRef.current = favoritePulse
  }, [favoritePulse])

  const unreadCountChats = data?.pages.reduce((acc) => {
    return acc
  }, 0)

  const menuItems: IMenuItem[] = [
    {
      href: '/',
      icon: <MapIcon />,
      text: t('map'),
    },
    {
      href: '/cabinet',
      icon: <HomeIcon />,
      text: t('cabinet'),
    },
    {
      href: '/profile',
      icon: <UserIcon />,
      text: t('profile'),
    },
    {
      href: '/wishlist',
      icon: <HeartFillIcon />,
      text: t('favorites'),
      pulse: true,
    },
    {
      href: '/messenger',
      icon: <MailIcon />,
      text: t('messenger'),
      notificationCount: unreadCountChats,
    },
    {
      href: '/notifications',
      icon: <NotificationIcon />,
      text: t('notifications'),
      notificationCount: unreadCount >= 10 ? '10+' : unreadCount,
    },
    {
      href: '/agreement',
      icon: <FileIcon />,
      text: t('agreement_menu'),
    },
    {
      href: '/support',
      icon: <QuestionIcon />,
      text: t('support'),
    },
  ]

  return (
    <nav aria-label={t('nav.sidebar_label')} className="flex flex-col">
      <ul className="flex flex-col gap-px m-0 p-0 list-none">
        {menuItems.map((item, index) => {
          const isActive = location.pathname === item.href

          return (
            <li
              key={index}
              className={clsx(
                'last:border-0 last:border-t last:border-solid last:border-ink/[0.08]',
                collapsed ? 'pr-0' : 'pr-4',
              )}
            >
              {/* the open section: tinted and marked for screen readers */}
              <Link
                to={item.href}
                aria-current={isActive ? 'page' : undefined}
                aria-label={collapsed ? item.text : undefined}
                className={clsx(
                  'flex items-center w-full min-h-12 text-base transition-colors duration-200',
                  collapsed
                    ? 'gap-2 px-3 py-3 justify-center rounded-xl'
                    : 'gap-4 px-8 py-3 rounded-xl rounded-bl-none rounded-tl-none',
                  isActive
                    ? 'font-extrabold bg-brand-soft text-brand-ink fill-brand-ink'
                    : 'font-bold text-ink fill-ink hover:bg-page',
                )}
                title={collapsed ? item.text : undefined}
              >
                <span
                  className={clsx(
                    'icon-button w-4 h-4 relative transition-transform duration-300',
                    item.pulse && pulsing && 'scale-[1.6] fill-danger-strong',
                  )}
                >
                  {item.icon}
                  {collapsed && !!item.notificationCount && item.notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-danger-strong rounded-full" />
                  )}
                </span>
                {!collapsed && (
                  <>
                    <span className="self-stretch my-auto">{item.text}</span>
                    {!!item.notificationCount && item.notificationCount > 0 && (
                      <span className="icon px-1.5 my-auto min-w-6 h-6 text-xs font-bold text-center text-white bg-danger-strong rounded-full">
                        {item.notificationCount}
                      </span>
                    )}
                  </>
                )}
              </Link>
            </li>
          )
        })}
      </ul>
      <div className="w-full h-px bg-ink/[0.08]" />
    </nav>
  )
}

export default Navigation
